import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AnalyseHome from './AnalyseHome';
import AnalyseWizard from './AnalyseWizard';
import AnalyseResults from './AnalyseResults';
import AnalyseHistory from './AnalyseHistory';
import MODEL from '../../data/cap_analyse_model';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalysePage = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('home');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedMode, setSelectedMode] = useState('qualite');
  const [isRapid, setIsRapid] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [financialData, setFinancialData] = useState(null);

  const authHeader = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  // Initial load — fetch user's analyses from MongoDB
  useEffect(() => {
    if (!user?.id || !token) return;
    axios.get(`${API}/analyses`, { headers: authHeader() })
      .then(r => setSavedAnalyses(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSavedAnalyses([]));
  }, [user?.id, token, authHeader]);

  useEffect(() => {
    const ticker = searchParams.get('ticker');
    const name = searchParams.get('name');
    if (ticker && name) setSelectedCompany({ symbol: ticker, name });
  }, [searchParams]);

  // Persist a single analysis upstream + update local list.
  const persistAnalysis = useCallback(async (analysis) => {
    if (!analysis?.id || !token) return;
    try {
      await axios.put(`${API}/analyses/${analysis.id}`, analysis, { headers: authHeader() });
    } catch { /* swallow network errors — local state already updated */ }
  }, [token, authHeader]);

  // Replace the in-memory list (used for batch updates like sort) without re-saving everything.
  const refreshSavedAnalyses = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/analyses`, { headers: authHeader() });
      setSavedAnalyses(Array.isArray(r.data) ? r.data : []);
    } catch { /* ignore */ }
  }, [token, authHeader]);

  const startAnalysis = useCallback((company, mode, rapid) => {
    setCurrentAnalysis({
      id: crypto.randomUUID(),
      ticker: company.symbol,
      companyName: company.name,
      mode,
      isRapid: rapid,
      date: new Date().toISOString().split('T')[0],
      status: 'in_progress',
      currentSectionIdx: 0,
      scores: {},
      aiUsage: {},
      eliminatoryAlerts: [],
    });
    setFinancialData(null);
    setView('wizard');
  }, []);

  const resumeAnalysis = useCallback((analysis) => {
    setCurrentAnalysis(analysis);
    setSelectedCompany({ symbol: analysis.ticker, name: analysis.companyName });
    setSelectedMode(analysis.mode);
    setIsRapid(analysis.isRapid);
    setView('wizard');
  }, []);

  const completeAnalysis = useCallback(async (analysis) => {
    const completed = { ...analysis, status: 'completed', date: new Date().toISOString().split('T')[0] };
    setCurrentAnalysis(completed);
    setSavedAnalyses(prev => [completed, ...prev.filter(a => a.id !== completed.id)]);
    await persistAnalysis(completed);
    setView('results');
    // Track analysis for badge system
    if (token) {
      fetch(`${API}/community/track-analysis`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
    }
  }, [persistAnalysis, token]);

  const saveAndExit = useCallback(async (analysis) => {
    setSavedAnalyses(prev => [analysis, ...prev.filter(a => a.id !== analysis.id)]);
    await persistAnalysis(analysis);
  }, [persistAnalysis]);

  const deleteAnalysis = useCallback(async (id) => {
    setSavedAnalyses(prev => prev.filter(a => a.id !== id));
    try {
      await axios.delete(`${API}/analyses/${id}`, { headers: authHeader() });
    } catch { /* ignore */ }
  }, [authHeader]);

  const relaunchAnalysis = useCallback(async (original, versionName) => {
    const today = new Date().toISOString().split('T')[0];
    const newAnalysis = {
      id: crypto.randomUUID(),
      ticker: original.ticker,
      companyName: original.companyName,
      mode: original.mode,
      isRapid: original.isRapid,
      date: today,
      status: 'in_progress',
      currentSectionIdx: 0,
      scores: { ...original.scores },
      aiUsage: {},
      eliminatoryAlerts: [],
      versionName: versionName || `Version ${today}`,
    };
    setSavedAnalyses(prev => [newAnalysis, ...prev]);
    await persistAnalysis(newAnalysis);
    setCurrentAnalysis(newAnalysis);
    setSelectedCompany({ symbol: original.ticker, name: original.companyName });
    setSelectedMode(original.mode);
    setIsRapid(original.isRapid);
    setFinancialData(null);
    setView('wizard');
  }, [persistAnalysis]);

  const viewResults = useCallback((analysis) => {
    setCurrentAnalysis(analysis);
    setSelectedCompany({ symbol: analysis.ticker, name: analysis.companyName });
    setSelectedMode(analysis.mode);
    setIsRapid(analysis.isRapid);
    setView('results');
  }, []);

  const viewHistory = useCallback((ticker) => {
    const analyses = savedAnalyses.filter(a => a.ticker === ticker && a.status === 'completed');
    if (analyses.length > 0) setSelectedCompany({ symbol: ticker, name: analyses[0].companyName });
    setView('history');
  }, [savedAnalyses]);

  // Used by AnalyseResults onSave callback to persist updates (eg. user-edited fields).
  const onSaveFromResults = useCallback(async (analysis) => {
    setSavedAnalyses(prev => [analysis, ...prev.filter(x => x.id !== analysis.id)]);
    await persistAnalysis(analysis);
  }, [persistAnalysis]);

  return (
    <div className="min-h-screen bg-cap-bg" data-testid="analyse-page">
      {view === 'home' && (
        <AnalyseHome
          model={MODEL}
          selectedMode={selectedMode}
          setSelectedMode={setSelectedMode}
          isRapid={isRapid}
          setIsRapid={setIsRapid}
          selectedCompany={selectedCompany}
          setSelectedCompany={setSelectedCompany}
          savedAnalyses={savedAnalyses}
          onStart={startAnalysis}
          onResume={resumeAnalysis}
          onDelete={deleteAnalysis}
          onRelaunch={relaunchAnalysis}
          onViewResults={viewResults}
          onViewHistory={viewHistory}
          onRefresh={refreshSavedAnalyses}
        />
      )}
      {view === 'wizard' && currentAnalysis && (
        <AnalyseWizard
          model={MODEL}
          analysis={currentAnalysis}
          setAnalysis={setCurrentAnalysis}
          financialData={financialData}
          setFinancialData={setFinancialData}
          onComplete={completeAnalysis}
          onSaveAndExit={saveAndExit}
          onBack={() => setView('home')}
          apiUrl={API}
          authHeader={authHeader}
        />
      )}
      {view === 'results' && currentAnalysis && (
        <AnalyseResults
          model={MODEL}
          analysis={currentAnalysis}
          financialData={financialData}
          onBack={() => setView('home')}
          onRedo={() => startAnalysis(
            { symbol: currentAnalysis.ticker, name: currentAnalysis.companyName },
            currentAnalysis.mode,
            currentAnalysis.isRapid
          )}
          onSave={onSaveFromResults}
          apiUrl={API}
          userId={user?.id}
          authHeader={authHeader}
        />
      )}
      {view === 'history' && selectedCompany && (
        <AnalyseHistory
          model={MODEL}
          ticker={selectedCompany.symbol}
          companyName={selectedCompany.name}
          analyses={savedAnalyses.filter(a => a.ticker === selectedCompany.symbol && a.status === 'completed')}
          onBack={() => setView('home')}
          onViewResults={viewResults}
        />
      )}
    </div>
  );
};

export default AnalysePage;
